import { z } from "zod";
import { overlayOrArraySchema } from "./overlay.schema.js";

/**
 * Text style presets for text items
 */
export const textStyleSchema = z
  .enum(["headline", "headline_large", "body", "caption"])
  .describe(
    'Text style preset: "headline" for section-level statements, "headline_large" for hero moments, "body" for standard copy (default), "caption" for fine print.',
  );

/**
 * Animation styles for items and groups
 */
export const animationStyleSchema = z
  .enum([
    "fade",
    "typewriter",
    "none",
    "word-by-word",
    "rise",
    "flash-in",
    "scramble",
    "blur-in",
    "cascade",
  ])
  .describe(
    'Text reveal animation: "fade" (default), "typewriter", "word-by-word", "rise", "flash-in", "scramble", "blur-in", "cascade", or "none" to render instantly.',
  );

/**
 * Speed multiplier for text animations, shared by items, style objects, and groups.
 */
export const animationSpeedSchema = z
  .number()
  .positive()
  .describe(
    "Animation speed multiplier: 1 = normal, 2 = twice as fast, 0.5 = half speed.",
  );

/**
 * Object form of text style — allows per-item overrides for color, size, and animation
 */
export const textStyleObjectSchema = z
  .object({
    preset: textStyleSchema
      .optional()
      .describe('Base style preset the overrides apply to (default: "body").'),
    textSize: z
      .string()
      .optional()
      .describe('CSS font-size override (e.g. "1.5rem", "24px").'),
    textColor: z
      .string()
      .optional()
      .describe('CSS color override (e.g. "#0e3f05").'),
    textAlign: z
      .enum(["left", "center", "right"])
      .optional()
      .describe("CSS text-align override."),
    animationStyle: animationStyleSchema
      .optional()
      .describe("Per-item animation style override."),
    animationSpeed: animationSpeedSchema.optional(),
  })
  .describe(
    "Object form of text style: a preset plus optional per-item overrides for size, color, alignment, and animation.",
  );

/**
 * Union: style can be a preset string OR a style object
 */
export const textStyleValueSchema = z
  .union([textStyleSchema, textStyleObjectSchema])
  .describe(
    'Text style: either a preset name ("headline", "headline_large", "body", "caption") or a style object with per-item overrides.',
  );

/**
 * Card styles for shareable insight cards
 */
export const cardStyleSchema = z
  .enum(["metric", "insight", "quote", "comparison"])
  .describe("Visual style for a shareable insight card.");

/**
 * Card configuration for shareable insight cards
 */
export const cardConfigSchema = z
  .object({
    title: z.string().describe("Short headline for the card (5-10 words)."),
    body: z
      .string()
      .describe("2-3 sentence summary (20-40 words, supports placeholders)."),
    metric: z
      .string()
      .optional()
      .describe("Optional callout metric (supports placeholders)."),
    style: cardStyleSchema.describe("Visual style for the card."),
  })
  .describe("Configuration for a shareable insight card.");

/**
 * Quiz data structure for interactive questions
 */
export const quizDataSchema = z
  .object({
    question: z.string().describe("The question shown to the viewer."),
    answers: z
      .array(z.string())
      .min(2)
      .max(4)
      .describe("Answer choices (2-4 options)."),
    correctIndex: z
      .number()
      .int()
      .min(0)
      .describe(
        "0-based index of the correct answer within the answers array.",
      ),
  })
  .superRefine((data, ctx) => {
    if (data.correctIndex >= data.answers.length) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `correctIndex (${data.correctIndex}) must be less than answers array length (${data.answers.length})`,
        path: ["correctIndex"],
      });
    }
  })
  .describe(
    "Quiz content: a question, 2-4 answer choices, and the correct index.",
  );

/**
 * Chart item configuration (advanced data styling)
 * Matches DataShorts ItemConfig interface
 */
export const itemConfigSchema = z
  .object({
    value: z
      .union([z.number(), z.string()])
      .describe(
        'The data value: a number, or a string for placeholder tokens (e.g. "{{hours_saved}}").',
      ),
    color: z
      .string()
      .optional()
      .describe("Override the bar color for this value (CSS color)."),
    dataItemStyle: z
      .enum(["striped", "solid"])
      .optional()
      .describe('Visual styling for this bar: "striped" or "solid".'),
    valueLabels: z
      .boolean()
      .optional()
      .describe("Show/hide the value label on this bar."),
  })
  .describe(
    "Advanced per-value chart styling (DataShorts ItemConfig): wraps a value with optional color, striped/solid style, and value-label control.",
  );

/**
 * Chart frame data structure
 * Mirrors the DataShorts frames format:
 * [{ label: "Store A", apples: 6, oranges: 3 }, { label: "Store B", apples: 2, oranges: 6 }]
 *
 * Frame values can be:
 * - number or string: simple values
 * - ItemConfig object: advanced styling with { value, dataItemStyle?, color?, valueLabels? }
 */
export const chartFrameSchema = z
  .object({
    label: z
      .string()
      .optional()
      .describe("Frame label shown while this frame is displayed."),
  })
  .catchall(
    z
      .union([z.string(), z.number(), itemConfigSchema])
      .describe(
        'Data series value, keyed by series name. Use a number for plain values, a string for placeholder tokens (e.g. "{{hours_saved}}"), or an ItemConfig object ({ value, color?, dataItemStyle?, valueLabels? }) for per-bar styling.',
      ),
  )
  .describe(
    'One chart frame in DataShorts format: an optional "label" plus one key per data series (e.g. { label: "Store A", apples: 6, oranges: 3 }).',
  );

/**
 * Bar chart configuration for animated visualizations
 */
export const barChartDataSchema = z
  .object({
    title: z
      .string()
      .optional()
      .describe("Optional chart title (not passed to DataShorts)."),
    frames: z
      .array(chartFrameSchema)
      .describe(
        "Array of frame objects in DataShorts native format; the chart animates between frames.",
      ),

    // DataShorts BarChartAnimator props (all optional)
    width: z
      .number()
      .optional()
      .describe(
        "Optional fixed width override in pixels. Omit for responsive sizing (chart fills its container); providing `width` opts out of that auto-sizing and may clip on smaller screens.",
      ),
    height: z
      .number()
      .optional()
      .describe("Chart height in pixels (default: 360)."),
    maxBarWidth: z
      .number()
      .optional()
      .describe(
        "Cap individual bar width in pixels. Bars exceeding this are clamped and centered.",
      ),
    duration: z
      .number()
      .optional()
      .describe("Animation duration in milliseconds (default: 3000)."),
    animateEach: z
      .boolean()
      .optional()
      .describe("Stagger bar animations (default: true)."),
    padding: z
      .number()
      .optional()
      .describe("Chart padding in pixels (default: 0)."),
    advanceDelay: z
      .number()
      .optional()
      .describe("Pause between frame transitions in milliseconds."),
    easing: z
      .string()
      .optional()
      .describe(
        'CSS timing function for bar animation, e.g. "ease" (default), "linear", "ease-in-out", or "cubic-bezier(0.4, 0, 0.2, 1)".',
      ),
    loopMode: z
      .enum(["normal", "alternate"])
      .optional()
      .describe(
        'Loop playback mode: "normal" restarts from the first frame, "alternate" plays frames back and forth.',
      ),
    iterations: z
      .union([z.number(), z.literal("infinite")])
      .optional()
      .describe('Loop count: a number of iterations or "infinite".'),
    stagger: z
      .number()
      .optional()
      .describe("Delay between visual elements in milliseconds."),
    labels: z
      .boolean()
      .optional()
      .describe("Show/hide data series labels (default: true)."),
    labelFontSize: z
      .number()
      .positive()
      .optional()
      .describe(
        "Font size in px for series and value labels (default: 24).",
      ),
    valueLabels: z.boolean().optional().describe("Show value labels on bars."),
    valueLabelPrecision: z
      .number()
      .optional()
      .describe("Significant figures for value labels (default: 3)."),
    valueLabelPrefix: z
      .string()
      .optional()
      .describe('Prepended to each value label, e.g. "$" (default: "").'),
    valueLabelSuffix: z
      .string()
      .optional()
      .describe(
        'Appended to each value label, e.g. "%" (default: ""). For unit-range values (values ≥1000 still abbreviate to k/M).',
      ),
    sort: z
      .enum(["none", "valueDesc", "valueAsc"])
      .optional()
      .describe(
        'Bar order: "none" (default, frame key order) or by value. Value sorts survive JSONB key-order loss.',
      ),
    hold: z
      .object({
        start: z
          .number()
          .optional()
          .describe("Hold the first frame for this fraction of the duration."),
        end: z
          .number()
          .optional()
          .describe("Hold the last frame for this fraction of the duration."),
      })
      .optional()
      .describe("Hold the first/last frame for a percentage of the duration."),
    color: z
      .string()
      .optional()
      .describe("CSS color for bars (default: currentColor)."),
    dataItemStyle: z
      .enum(["striped", "solid"])
      .optional()
      .describe(
        'Default visual styling for bars: "striped" or "solid". Individual bars can override this via ItemConfig.',
      ),
    autoPlay: z
      .boolean()
      .optional()
      .describe("Start the animation immediately (default: true)."),
    className: z.string().optional().describe("Additional CSS classes."),

    // ZOD 4 FIX: z.record now requires (keySchema, valueSchema)
    style: z
      .record(z.string(), z.unknown())
      .optional()
      .describe(
        "Inline React style object applied to the chart's <svg> root element. It does not affect label text size; use labelFontSize for that.",
      ),
  })
  .catchall(z.unknown()) // Allow any additional DataShorts props
  .describe(
    "Animated bar chart configuration in DataShorts format: frames plus optional BarChartAnimator props. Unknown extra keys pass through to DataShorts.",
  );

/**
 * Base properties shared by all item types
 */
const baseItemSchema = z.object({
  overlay: overlayOrArraySchema
    .optional()
    .describe("Optional animation overlay (single or array) for this item."),
  marginTop: z
    .string()
    .optional()
    .describe(
      'CSS margin-top override for this item (e.g. "0", "0.5rem", "2rem"). Overrides the default inter-item spacing.',
    ),
});

/**
 * Session action override shared by interactive items. Omit for the item's
 * default action, provide a string to override it, or false to disable tracking.
 */
const actionSchema = z.union([z.string(), z.literal(false)]);

/**
 * Text item - styled text with markdown support
 */
const textItemSchema = baseItemSchema
  .extend({
    type: z.literal("text"),
    content: z
      .string()
      .describe(
        "The text to display. Supports markdown and {{placeholder}} tokens.",
      ),
    style: textStyleValueSchema.describe(
      "Preset string or style object with per-item overrides.",
    ),
    animationStyle: animationStyleSchema
      .optional()
      .describe("Optional animation style override for this item."),
    animationSpeed: animationSpeedSchema.optional(),
    contentAuthor: z
      .string()
      .optional()
      .describe(
        'Author attribution: omit for no badge, "ai" for the AI badge, any other string for a human name badge.',
      ),
  })
  .describe("Text item: styled text with markdown and placeholder support.");

/**
 * CTA Button item - call-to-action button
 */
const ctaButtonItemSchema = baseItemSchema
  .extend({
    type: z.literal("cta_button"),
    content: z.string().optional().describe("Optional button label."),
    url: z
      .string()
      .superRefine((value, ctx) => {
        // Allow empty string for non-link buttons
        if (value === "") {
          return;
        }
        try {
          const url = new URL(value, "http://dummy");
          if (
            url.protocol !== "http:" &&
            url.protocol !== "https:" &&
            url.protocol !== "mailto:"
          ) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: `URL protocol must be http:, https:, or mailto:, got ${url.protocol}`,
            });
          }
        } catch {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Invalid URL format",
          });
        }
      })
      .optional()
      .describe(
        "Optional URL opened in a new tab on click (http:, https:, or mailto:). Empty string for a non-link button.",
      ),
    icon: z
      .string()
      .optional()
      .describe(
        'Optional Font Awesome icon as "<prefix> <name>" (e.g. "fass envelope", "fausb chart-simple"). A bare name defaults to the "fass" prefix; a leading "fa-" on the name is stripped.',
      ),
    action: actionSchema
      .optional()
      .describe(
        'Session action type emitted on click. Omit for the default "buttonClick", provide a string (e.g. "feedback_thumbsup") to override, or false to disable engagement tracking for this button.',
      ),
  })
  .describe("Call-to-action button item.");

/**
 * CTA Bookmark item - bookmark/save button
 */
const ctaBookmarkItemSchema = baseItemSchema
  .extend({
    type: z.literal("cta_bookmark"),
    content: z.string().optional().describe("Optional custom label."),
  })
  .describe("Bookmark/save button item.");

/**
 * CTA Comment item - comment/note prompt
 */
const ctaCommentItemSchema = baseItemSchema
  .extend({
    type: z.literal("cta_comment"),
    placeholder: z
      .string()
      .optional()
      .describe("Optional placeholder text for the comment form."),
    action: actionSchema
      .optional()
      .describe(
        'Session action type emitted when a note is saved. Omit for the default "commentSave", provide a string to override, or false to disable engagement tracking for this comment.',
      ),
  })
  .describe("Comment/note prompt item.");

/**
 * Table of Contents item - navigation menu
 */
const tableOfContentsItemSchema = baseItemSchema
  .extend({
    type: z.literal("table_of_contents"),
    selectSections: z
      .boolean()
      .optional()
      .describe(
        "When true, the viewer can select which sections to include before continuing.",
      ),
  })
  .describe(
    "Table of contents item: a navigation menu over the narrative's sections.",
  );

/**
 * Quiz item - interactive question
 */
const quizItemSchema = baseItemSchema
  .extend({
    type: z.literal("quiz"),
    quiz: quizDataSchema,
    action: actionSchema
      .optional()
      .describe(
        'Session action type emitted on answer. Omit for the default "quizClick", provide a string to override, or false to disable engagement tracking for this quiz.',
      ),
  })
  .describe("Interactive quiz item.");

/**
 * Bar Chart item - animated bar chart visualization
 */
const barChartItemSchema = baseItemSchema
  .extend({
    type: z.literal("bar_chart"),
    barChart: barChartDataSchema,
  })
  .describe("Animated bar chart item (DataShorts).");

/**
 * Highlights Gallery item - gallery of bookmarked insights
 */
const highlightsGalleryItemSchema = baseItemSchema
  .extend({
    type: z.literal("highlights_gallery"),
    showAllCards: z
      .boolean()
      .optional()
      .describe(
        "Show all cards rather than only the ones the viewer reacted to.",
      ),
  })
  .describe("Gallery of the viewer's bookmarked/highlighted insights.");

/**
 * Media showcase display configuration - what renders on screen
 */
export const mediaShowcaseDisplaySchema = z
  .object({
    type: z
      .enum(["image", "lottie"])
      .describe(
        'What renders on screen: a static "image" or a "lottie" animation.',
      ),
    src: z.string().describe("Path to the image or Lottie JSON."),
    overlay: overlayOrArraySchema
      .optional()
      .describe("Lottie overlays composited on top of the display media."),
  })
  .describe("What a media showcase entry renders on screen.");

/**
 * Media showcase content - per-item entry
 */
export const mediaShowcaseContentSchema = z
  .object({
    display: mediaShowcaseDisplaySchema,
    alt: z.string().optional().describe("Accessibility text for the media."),
    label: z
      .string()
      .optional()
      .describe("Optional caption shown below the content."),
    shareAsset: z
      .object({
        src: z
          .string()
          .describe("Static file to download/share (e.g. PNG, PDF)."),
        filename: z
          .string()
          .optional()
          .describe("Override the download filename."),
      })
      .describe("The static asset that download/share actions use."),
    shareText: z.string().optional().describe("Per-item share text override."),
    shareTitle: z
      .string()
      .optional()
      .describe("Per-item share title override."),
  })
  .describe(
    "One media showcase entry: the on-screen display plus the shareable asset behind it.",
  );

/**
 * Media showcase data - item-level config
 */
export const mediaShowcaseDataSchema = z
  .object({
    items: z
      .array(mediaShowcaseContentSchema)
      .min(1)
      .describe(
        "Showcase entries: 1 item renders standalone, 2+ render as a carousel.",
      ),
    shareText: z
      .string()
      .optional()
      .describe("Default share text for all items."),
    shareTitle: z
      .string()
      .optional()
      .describe("Default share title for all items."),
    showDownload: z
      .boolean()
      .optional()
      .describe("Show the download button (default: true)."),
    showShare: z
      .boolean()
      .optional()
      .describe("Show the share button (default: true)."),
    shareLabel: z
      .string()
      .optional()
      .describe('Custom label for the share button (default: "Share").'),
    downloadLabel: z
      .string()
      .optional()
      .describe('Custom label for the download button (default: "Download").'),
    showDownloadAll: z
      .boolean()
      .optional()
      .describe('Show "Download All" for carousels (default: true).'),
    downloadAllFilename: z
      .string()
      .optional()
      .describe('Zip filename used by "Download All".'),
  })
  .describe(
    "Media showcase configuration: entries plus share/download controls.",
  );

/**
 * Media Showcase item - rich media display with download/share
 */
const mediaShowcaseItemSchema = baseItemSchema
  .extend({
    type: z.literal("media_showcase"),
    mediaShowcase: mediaShowcaseDataSchema,
  })
  .describe("Rich media display item with download/share actions.");

/**
 * Music Choice item - presents two buttons so the viewer can choose to play
 * with background music or continue without. Advances the narrative on either choice.
 */
const musicChoiceItemSchema = baseItemSchema
  .extend({
    type: z.literal("music_choice"),
    playLabel: z
      .string()
      .optional()
      .describe('Override for the "Play with Music" button label.'),
    skipLabel: z
      .string()
      .optional()
      .describe('Override for the "Continue without" button label.'),
    playIcon: z
      .string()
      .optional()
      .describe(
        'Font Awesome icon for the play button (e.g. "fausb music"); omit for the default icon, empty string for none.',
      ),
    skipIcon: z
      .string()
      .optional()
      .describe(
        "Font Awesome icon for the skip button; omit or empty string for none.",
      ),
  })
  .describe(
    "Music choice item: two buttons letting the viewer play with background music or continue without; either choice advances the narrative.",
  );

/**
 * Discriminated union of all item types
 * This ensures type safety and proper validation
 */
export const insightItemSchema = z
  .discriminatedUnion("type", [
    textItemSchema,
    ctaButtonItemSchema,
    ctaBookmarkItemSchema,
    ctaCommentItemSchema,
    tableOfContentsItemSchema,
    quizItemSchema,
    barChartItemSchema,
    highlightsGalleryItemSchema,
    mediaShowcaseItemSchema,
    musicChoiceItemSchema,
  ])
  .describe(
    'A content item within a group, discriminated by "type": text, cta_button, cta_bookmark, cta_comment, table_of_contents, quiz, bar_chart, highlights_gallery, media_showcase, or music_choice.',
  );

/**
 * Known item type literals — derived from the discriminated union so the
 * sanitizer's allowlist always tracks the schema. Used to filter
 * unrecognized item types before Zod rejects the whole narrative.
 */
export const KNOWN_ITEM_TYPES = insightItemSchema.options.map(
  (option) => option.shape.type.value,
) as readonly string[];

/**
 * Known animation style literals — used by the sanitizer to replace
 * unrecognized values with undefined (the field is always optional).
 */
export const KNOWN_ANIMATION_STYLES =
  animationStyleSchema.options as readonly string[];

/**
 * Known text style presets — used by the sanitizer to replace
 * unrecognized preset strings with 'body' (safe default).
 */
export const KNOWN_TEXT_STYLES = textStyleSchema.options as readonly string[];

// Export inferred types
export type TextStyle = z.infer<typeof textStyleSchema>;
export type TextStyleObject = z.infer<typeof textStyleObjectSchema>;
export type TextStyleValue = z.infer<typeof textStyleValueSchema>;
export type AnimationStyle = z.infer<typeof animationStyleSchema>;
export type CardStyle = z.infer<typeof cardStyleSchema>;
export type CardConfig = z.infer<typeof cardConfigSchema>;
export type QuizData = z.infer<typeof quizDataSchema>;
export type ChartFrame = z.infer<typeof chartFrameSchema>;
export type BarChartData = z.infer<typeof barChartDataSchema>;
export type MediaShowcaseDisplay = z.infer<typeof mediaShowcaseDisplaySchema>;
export type MediaShowcaseContent = z.infer<typeof mediaShowcaseContentSchema>;
export type MediaShowcaseData = z.infer<typeof mediaShowcaseDataSchema>;
export type InsightItem = z.infer<typeof insightItemSchema>;
